/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable no-template-curly-in-string */

import { useContext, useEffect, useRef, useState } from 'react'
import { Form } from '@unform/web'
import {
  DefaultButton,
  ButtonNext,
  Path,
  MaskedInput2,
  UploadFile2,
  Select2,
} from 'components/index'
import { UserContext } from 'context/userContext'
import * as Yup from 'yup'
import { setLocale } from 'yup'
import { Icon } from 'assets/index'
import { useFetch } from 'hooks'
import Tooltip from 'react-tooltip'
import { numberToMoney } from 'helper/utils'
import validate from 'helper/validate.js'
import { Container, InputError, FilesContainer } from './style'
import Distribution from './Distribution'

interface IObj {
  [_key: string]: any
}

interface ValidationErrors {
  [key: string]: string
}

setLocale({
  mixed: {
    default: 'Não é válido',
  },
  number: {
    min: 'Mínimo de ${min} caracteres',
  },
  string: {
    min: 'Mínimo de ${min} caracteres',
  },
})

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const pixKeyOptions = [
  { label: 'CPF', value: 'CPF' },
  { label: 'CNPJ', value: 'CNPJ' },
  { label: 'E-mail', value: 'EMAIL' },
  { label: 'Telefone', value: 'PHONE' },
  { label: 'Aleatória', value: 'EVP' },
]
const schemaNewEnterprise = Yup.object()
  .shape({
    name: Yup.string().min(1).required('Campo obrigatório'),
    shortName: Yup.string().min(1).required('Campo obrigatório'),
    document: Yup.string()
      .min(18)
      .max(18)
      .required('Campo obrigatório')
      .test('document', 'CNPJ inválido', (value) => {
        return validate.numbers.cnpj(value)
      }),
    email: Yup.string()
      .min(3)
      .matches(emailRegex, 'Email não é válido')
      .required('Campo obrigatório'),
    cellphone: Yup.string().min(15).required('Campo obrigatório'),
    tick: Yup.string().min(1).required('Campo obrigatório'),
    amount: Yup.number().min(1).required('Campo obrigatório'),
    meterValue: Yup.number().min(1).required('Campo obrigatório'),
    meters: Yup.number().min(1).required('Campo obrigatório'),
    quotaValue: Yup.number().min(1).required('Campo obrigatório'),
    // documentList: Yup.array().min(2).required('Campo obrigatório'),
    activateAt: Yup.string().min(10).required('Campo obrigatório'),
    readyAt: Yup.string().min(10).required('Campo obrigatório'),
    apiKey: Yup.string()
      .matches(/^\$aact_[A-Za-z0-9+/=_-]+$/, 'chave API inválida')
      .required('Campo obrigatório'),
    address: Yup.object().shape({
      postalCode: Yup.string().min(9).required('Campo obrigatório'),
      address: Yup.string().min(3).required('Campo obrigatório'),
      number: Yup.string().min(1).required('Campo obrigatório'),
      complement: Yup.string().min(1).required('Campo obrigatório'),
      province: Yup.string().min(1).required('Campo obrigatório'),
      state: Yup.string().required('Campo obrigatório'),
      city: Yup.string().min(3).required('Campo obrigatório'),
    }),

    pixType: Yup.string().required('Tipo de chave é obrigatório'),

    pix: Yup.object().shape({
      institution: Yup.string().required('Banco é obrigatório'),
      account: Yup.string().required('Conta é obrigatória'),
      branch: Yup.string().required('Agência é obrigatória'),
    }),

    key: Yup.string()
      .nullable()
      .test('pix-validation', function (value) {
        const { pixType } = this.parent

        // Se um foi preenchido e o outro não
        if (pixType && !value) {
          return this.createError({ message: 'Chave PIX é obrigatória' })
        }

        if (!pixType && value) {
          return this.createError({ message: 'Tipo de chave é obrigatória' })
        }

        if (!pixType || !value) return true

        if (pixType === 'CNPJ' && !validate.numbers.cnpj(value)) {
          return this.createError({ message: 'CNPJ inválido' })
        }

        if (pixType === 'CPF' && !validate.numbers.cpf(value)) {
          return this.createError({ message: 'CPF inválido' })
        }

        if (pixType === 'PHONE' && !validate.numbers.cellphonePix(value)) {
          return this.createError({ message: 'Telefone inválido' })
        }

        if (pixType === 'EMAIL' && !validate.string.email(value)) {
          return this.createError({ message: 'Email inválido' })
        }

        return true
      }),
  })
  .typeError('Este input deve ser preenchido corretamente')

const documentosObrigatorios: any = [
  { id: 'mcs', name: 'Minuta do contrato SCP', mandatory: false },
  { id: 'ctrsc', name: 'Contrato Social', mandatory: false },
  { id: 'alv', name: 'Alvará', mandatory: false },
  { id: 'cnpjspe', name: 'CNPJ de SPE', mandatory: false },
  { id: 'regim', name: 'Registro de imóvel', mandatory: false },
  { id: 'lte', name: 'Lâmina técnica', mandatory: false },
]
const contratosObrigatorios: any = []

export default function NovoEmpreendimento() {
  const { data, setData, setPage } = useContext(UserContext)
  const formRef = useRef<HTMLFormElement | any>(null)
  const [contratos, setNewContrato] = useState<any>(contratosObrigatorios)
  const [documentos, setNewDocumento] = useState<any>(documentosObrigatorios)
  const [attachments, setAttachments] = useState<any>([])
  const [distribution, setDistribution] = useState<any[]>([])
  const [showNewFile, setShowNewFile] = useState<string | undefined>()
  const [distributionErro, setDistributionErro] = useState<boolean | string>(
    false
  )
  const api = useFetch()

  const opt = {
    accept: '.pdf',
    info: '• Documento no formato PDF',
    icon: 'PDF.svg',
    label: 'Upload de PDF',
  }

  useEffect(() => {
    const vgv = Number(data?.meterValue) * Number(data?.meters)
    const maxSupply = vgv / data?.quotaValue

    setData((old: any) => ({
      ...old,
      vgv: Number.isNaN(vgv) ? null : `R$ ${numberToMoney(vgv)}`,
      maxSupply: Number.isNaN(maxSupply) ? null : maxSupply,
    }))

    if (data?.meterValue && data?.quotaValue && data?.amount) {
      const obj = {
        unitName: null,
        unitMeters: null,
        unitValue: null,
        unitSupply: null,
      }
      const newArr = Array(data?.amount).fill(obj)
      setDistribution(newArr)
    }
  }, [data?.amount, data?.meters, data?.meterValue, data?.quotaValue])

  const handleAddFile = (type: string, name: string, file: File) => {
    const id = `doc_${Math.random()}`

    if (type === 'Novo contrato') {
      setNewContrato((old: any) => [...old, { id, name, file }])
    } else if (type === 'Novo anexo') {
      setAttachments((old: any) => [...old, { id, name, file }])
    } else {
      setNewDocumento((old: any) => [...old, { id, name, file }])
    }
  } // Adiciona um novo arquivo à lista de documentos

  const onPutCEP = (e: any) => {
    const n = e.target.value

    // Verifica se o CEP tem o comprimento necessário
    if (n.length < 9) return

    // Faz a chamada à API para buscar os dados do endereço
    api.fetch('get', null, `/app/get-address/${n}`).then((response) => {
      if (!response.data) return

      setData((cur: IObj) => ({
        ...cur,
        address: {
          ...cur.address,
          ...response.data, // Atualiza cidade e estado com os dados da API
          postalCode: n, // Sempre atualiza o CEP
        },
      }))
    })
  }

  const renderUploadFile = (items: any[], type: string) =>
    items.map((item, i) => (
      <div style={{ marginRight: 20, marginBottom: 20 }} key={item.id}>
        <UploadFile2
          mandatory={item?.mandatory}
          id={item.id}
          data-testid={`${type}-upload-file-${i}`}
          edit
          ready
          visualize
          file={item.file}
          options={opt}
          fileName={item.name}
          onChangeFileName={(name: string) => {
            if (type === 'document') {
              setNewDocumento((prev: any) =>
                prev.map((doc: any) =>
                  doc.id === item.id ? { ...doc, name } : doc
                )
              )
            }
            if (type === 'attachment') {
              setAttachments((prev: any) =>
                prev.map((doc: any) =>
                  doc.id === item.id ? { ...doc, name } : doc
                )
              )
            }
            if (type === 'contract') {
              setNewContrato((prev: any) =>
                prev.map((doc: any) =>
                  doc.id === item.id ? { ...doc, name } : doc
                )
              )
            }
          }}
          onChangeFile={(file: any) => {
            if (type === 'document') {
              setNewDocumento((prev: any) =>
                prev.map((doc: any) =>
                  doc.id === item.id ? { ...doc, file } : doc
                )
              )
            }
            if (type === 'attachment') {
              setAttachments((prev: any) =>
                prev.map((doc: any) =>
                  doc.id === item.id ? { ...doc, file } : doc
                )
              )
            }
            if (type === 'contract') {
              setNewContrato((prev: any) =>
                prev.map((doc: any) =>
                  doc.id === item.id ? { ...doc, file } : doc
                )
              )
            }
          }}
          onClickRemoveFile={(fileName: string) => {
            setData((old: any) => ({
              ...old,
              [`${type}List`]: old[`${type}List`].filter(
                (ct: any) => ct[0] !== fileName
              ),
            }))
            item.file = undefined
          }}
          onClickTrash={() => {
            if (type === 'contract') {
              setNewContrato((prev: any) =>
                prev.filter((ct: any) => ct.id !== item.id)
              )
            }
            if (type === 'document') {
              setNewDocumento((prev: any) =>
                prev.filter((doc: any) => doc.id !== item.id)
              )
              item.file = undefined
            }
            if (type === 'attachment') {
              setAttachments((prev: any) =>
                prev.filter((att: any) => att.id !== item.id)
              )
              item.file = undefined
            }
          }}
        />
      </div>
    ))

  useEffect(() => {
    const contratosComFile = contratos.filter(
      (contrato: { file: undefined }) => contrato.file !== undefined
    )
    const documentosComFile = documentos.filter(
      (documento: { file: undefined }) => documento.file !== undefined
    )
    const attachmentsComFile = attachments.filter(
      (attachment: any) => attachment.file !== undefined
    )

    const newDocumentList = documentosComFile.map((doc: any) => [
      doc.name,
      doc.file,
    ])
    const newContractList = contratosComFile.map((contract: any) => [
      contract.name,
      contract.file,
    ])
    const newAttachmentList = attachmentsComFile.map((attachment: any) => [
      attachment.name,
      attachment.file,
    ])
    setData((prevData: any) => ({
      ...prevData,
      contractList: newContractList,
      documentList: newDocumentList,
      attachmentList: newAttachmentList,
    }))
  }, [contratos, documentos, attachments])
  // Atualiza data de contratos e documentos q quando um novo arquivo é adicionado

  const handleSubmit = async () => {
    try {
      // Remove all previous errors
      formRef.current.setErrors({})
      setDistributionErro(false)

      await schemaNewEnterprise.validate(data, { abortEarly: false })

      const formatedDistribution = distribution.map((item: any) => {
        return [item.unitName, item.unitMeters, item.availableSupply]
      })

      setData((old: any) => {
        const { pixType, key, ...rest } = old

        return {
          ...rest,
          distribution: formatedDistribution,
          meterTotal: data?.meters,
          pix: {
            ...old.pix,
            key,
            type: pixType,
          },
        }
      })

      setPage(1)
    } catch (err) {
      const validationErrors: ValidationErrors = {}
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          if (error.path) {
            validationErrors[error.path] = error.message
          }
        })

        formRef.current.setErrors(validationErrors)
      }
    }
  }

  const [pixType, setPixType] = useState('')
  const [pixMask, setPixMask] = useState('')

  return (
    <Container>
      <Path className="path">
        <span className="path">
          {'Gestão de produtos >  Investimentos  > Novo empreendimento'}
        </span>
      </Path>
      <Form ref={formRef} onSubmit={() => console.log('submit')}>
        <div>
          <h1 className="new-token-title">Novo empreendimento</h1>

          <div className="new-token-subtitle">
            <h4>Dados do empreendimento</h4>
          </div>
          <div className="new-token-container">
            <MaskedInput2
              maxLength={64}
              large
              hasAsterisk
              dataTestId="name-input"
              type="text"
              handleChange={(e: any) => {
                setData((oldValue: any) => ({
                  ...oldValue,
                  name: e.target.value,
                }))
              }}
              placeholder="Nome completo do empreendimento"
              name="name"
              defaultValue={data?.name}
            />
            <MaskedInput2
              large
              maxLength={32}
              hasAsterisk
              dataTestId="shortName-input"
              type="text"
              handleChange={(e: any) => {
                setData((oldValue: any) => ({
                  ...oldValue,
                  shortName: e.target.value,
                }))
              }}
              placeholder="Nome resumido"
              name="shortName"
              defaultValue={data?.shortName}
            />
            <MaskedInput2
              large
              hasAsterisk
              dataTestId="sigla-input"
              type="text"
              maxLength={16}
              handleChange={(e: any) => {
                setData((oldValue: any) => ({
                  ...oldValue,
                  tick: e.target.value,
                }))
              }}
              placeholder="Sigla do empreendimento"
              name="tick"
              defaultValue={data?.tick}
            />
            <MaskedInput2
              large
              hasAsterisk
              dataTestId="cnpj-input"
              type="cnpj"
              handleChange={(e: any) => {
                setData((oldValue: any) => ({
                  ...oldValue,
                  document: e.target.value,
                }))
              }}
              placeholder="CNPJ"
              name="document"
              defaultValue={data?.document}
            />
            <MaskedInput2
              large
              hasAsterisk
              dataTestId="email-input"
              type="text"
              maxLength={64}
              handleChange={(e: any) => {
                setData((oldValue: any) => ({
                  ...oldValue,
                  email: e.target.value,
                }))
              }}
              placeholder="E-mail"
              name="email"
              defaultValue={data?.email}
            />
            <MaskedInput2
              large
              hasAsterisk
              dataTestId="phone-input"
              type="cell"
              handleChange={(e: any) => {
                setData((oldValue: any) => ({
                  ...oldValue,
                  cellphone: e.target.value,
                }))
              }}
              placeholder="Telefone"
              name="cellphone"
              defaultValue={data?.cellphone}
            />
            <MaskedInput2
              large
              hasAsterisk
              dataTestId="activateAt-input"
              type="date"
              handleChange={(e: any) => {
                setData((oldValue: any) => ({
                  ...oldValue,
                  activateAt: e.target.value,
                }))
              }}
              placeholder="Data de ativação do Empreendimento"
              name="activateAt"
              defaultValue={data?.activateAt}
            />
          </div>
          <div className="new-token-subtitle">
            <h4>Informações do Empreendimento</h4>
          </div>
          <div className="new-token-container">
            <MaskedInput2
              large
              hasAsterisk
              dataTestId="units-amount-input"
              type="numberMaxFiveHundred"
              handleChange={(e: any) => {
                setData((oldValue: any) => ({
                  ...oldValue,
                  amount: Number(e),
                }))
              }}
              placeholder="Número total de unidades"
              name="amount"
              defaultValue={data?.amount}
            />
            <MaskedInput2
              large
              hasAsterisk
              dataTestId="meters-input"
              type="number"
              handleChange={(e: any) => {
                setData((oldValue: any) => ({
                  ...oldValue,
                  meters: Number(e),
                }))
              }}
              placeholder="Quantidade total de M²"
              name="meters"
              defaultValue={data?.meters}
            />
            <MaskedInput2
              large
              dataTestId="valueM²-input"
              hasAsterisk
              type="money"
              handleChange={(e: any) => {
                setData((oldValue: any) => ({
                  ...oldValue,
                  meterValue: Number(e),
                }))
              }}
              placeholder="Preço do M²"
              name="meterValue"
              defaultValue={data?.meterValue}
            />
            <MaskedInput2
              large
              dataTestId="quota-price-input"
              type="money"
              handleChange={(e: any) => {
                setData((oldValue: any) => ({
                  ...oldValue,
                  quotaValue: Number(e),
                }))
              }}
              placeholder="Preço da cota"
              name="quotaValue"
              defaultValue={data?.quotaValue}
            />
            <MaskedInput2
              large
              dataTestId="readyAt-input"
              hasAsterisk
              type="date"
              handleChange={(e: any) => {
                setData((oldValue: any) => ({
                  ...oldValue,
                  readyAt: e.target.value,
                }))
              }}
              placeholder="Data de liberação"
              name="readyAt"
              defaultValue={data?.readyAt}
            />
            <MaskedInput2
              disabled
              large
              dataTestId="total-quota-input"
              type="text"
              handleChange={(e: any) => {
                console.log(e)
              }}
              placeholder="Total de cotas do empreendimento"
              name="maxSupply"
              defaultValue={data?.maxSupply}
            />
            <MaskedInput2
              disabled
              large
              dataTestId="vgv-input"
              type="text"
              handleChange={(e: any) => {
                console.log(e)
              }}
              placeholder="VGV"
              name="vgv"
              defaultValue={data?.vgv}
            />
          </div>
          {data?.meterValue > 0 && data?.quotaValue > 0 && data.amount > 0 && (
            <>
              <div className="new-token-subtitle">
                <h4>Distribuição</h4>
              </div>
              {distributionErro && <InputError>{distributionErro}</InputError>}
              <div className="new-token-container">
                <Distribution arr={distribution} setArr={setDistribution} />
              </div>
            </>
          )}
          <div className="new-token-subtitle">
            <h4>Endereço do Empreendimento</h4>
          </div>
          <div className="new-token-container">
            <MaskedInput2
              large
              hasAsterisk
              dataTestId="cep-input"
              type="cep"
              handleChange={(e: any) => onPutCEP(e)}
              placeholder="CEP"
              name="address.postalCode"
              defaultValue={data?.address?.postalCode}
            />
            <MaskedInput2
              large
              hasAsterisk
              dataTestId="province-input"
              type="text"
              maxLength={36}
              handleChange={(e: any) =>
                setData((oldValue: any) => ({
                  ...oldValue,
                  address: {
                    ...oldValue.address,
                    province: e.target.value,
                  },
                }))
              }
              placeholder="Bairro"
              name="address.province"
              defaultValue={data?.address?.province}
            />
            <MaskedInput2
              large
              hasAsterisk
              dataTestId="address-input"
              type="text"
              maxLength={225}
              handleChange={(e: any) =>
                setData((oldValue: any) => ({
                  ...oldValue,
                  address: {
                    ...oldValue.address,
                    address: e.target.value,
                  },
                }))
              }
              placeholder="Endereço com rua"
              name="address.address"
              defaultValue={data?.address?.address}
            />
            <MaskedInput2
              large
              hasAsterisk
              dataTestId="address-number-input"
              type="text"
              maxLength={24}
              handleChange={(e: any) =>
                setData((oldValue: any) => ({
                  ...oldValue,
                  address: {
                    ...oldValue.address,
                    number: e.target.value,
                  },
                }))
              }
              placeholder="Número"
              name="address.number"
              defaultValue={data?.address?.number}
            />
            <MaskedInput2
              large
              hasAsterisk
              dataTestId="complement-input"
              type="text"
              maxLength={36}
              handleChange={(e: any) =>
                setData((oldValue: any) => ({
                  ...oldValue,
                  address: {
                    ...oldValue.address,
                    complement: e.target.value,
                  },
                }))
              }
              placeholder="Complemento"
              name="address.complement"
              defaultValue={data?.address?.complement}
            />
            <div className="input-adress-container">
              <MaskedInput2
                type="text"
                small
                dataTestId="state-select"
                disabled
                hasAsterisk
                name="address.state"
                placeholder={'UF'}
                defaultValue={data?.address?.state}
              />
              <MaskedInput2
                type="text"
                small
                dataTestId="city-select"
                disabled
                hasAsterisk
                name="address.city"
                placeholder={'Cidade'}
                defaultValue={data?.address?.city}
              />
            </div>
          </div>
          <div className="new-token-subtitle">
            <h4>Dados bancários</h4>
          </div>
          <div className="new-token-container">
            <MaskedInput2
              type="text"
              hasAsterisk
              dataTestId="pix.institution"
              placeholder="Banco"
              name="pix.institution"
              defaultValue={data?.pix?.institution}
              handleChange={(e: any) =>
                setData((old: any) => ({
                  ...old,
                  pix: {
                    ...old.pix,
                    institution: e.target.value,
                  },
                }))
              }
            />

            <MaskedInput2
              type="account"
              hasAsterisk
              dataTestId="pix.account"
              placeholder="Conta"
              name="pix.account"
              defaultValue={data?.pix?.account}
              handleChange={(e: any) =>
                setData((old: any) => ({
                  ...old,
                  pix: {
                    ...old.pix,
                    account: e.target.value,
                  },
                }))
              }
            />

            <MaskedInput2
              type="agency"
              hasAsterisk
              dataTestId="pix.branch"
              placeholder="Agência"
              name="pix.branch"
              defaultValue={data?.pix?.branch}
              handleChange={(e: any) =>
                setData((old: any) => ({
                  ...old,
                  pix: {
                    ...old.pix,
                    branch: e.target.value,
                  },
                }))
              }
            />
            <Select2
              dataTestId="pixType"
              isClearable
              small
              hasAsterisk
              options={pixKeyOptions}
              name="pixType"
              placeholder="Tipo de chave"
              onChange={(e) => {
                const value = e?.value || ''

                setPixType(value)

                setData((old: any) => ({
                  ...old,
                  pixType: value,
                }))

                if (value === 'PHONE') setPixMask('cellWithInternational')
                else if (value === 'CPF') setPixMask('cpf')
                else if (value === 'CNPJ') setPixMask('cnpj')
                else setPixMask('')
              }}
              value={pixKeyOptions.find((option) => option.value === pixType)}
            />

            <MaskedInput2
              type={pixMask || 'text'}
              hasAsterisk
              dataTestId="pix.key"
              placeholder="Chave pix"
              name="key"
              defaultValue={data?.key}
              handleChange={(e: any) =>
                setData((old: any) => ({
                  ...old,
                  key: e.target.value,
                }))
              }
            />

            <div data-tip data-for={`chaveApi`}>
              <MaskedInput2
                large
                hasAsterisk
                dataTestId="apiKey-input"
                type="text"
                handleChange={(e: any) => {
                  setData((oldValue: any) => ({
                    ...oldValue,
                    apiKey: e.target.value,
                  }))
                }}
                placeholder="Chave de API"
                name="apiKey"
                defaultValue={data?.apiKey}
              />
            </div>
            <Tooltip id={`chaveApi`}>
              <span>
                Exemplo: $aact_YTU5YTE0M2M2N2I4MTliNzk0YTI5N2U5MzdjNWZ
                <br />
                mNDQ6OjAwMDAwMDAwMDAwMDA0OTYxNDc6OiRhYWNoXzZk
                <br />
                NTdhYWViLTRkNWEtNGQ4YS1iZTk5LWI5YjIwOTE4MTrsPQ==
              </span>
            </Tooltip>
          </div>
          <div className="new-token-subtitle">
            <h4>Detalhes Jurídicos - Upload de Documentos e Contratos</h4>
          </div>
          <div className="new-token-container">
            <DefaultButton
              small
              icon={Icon('plus.svg')}
              onClick={() => setShowNewFile('Novo documento')}
            >
              Documento
            </DefaultButton>
            <DefaultButton
              small
              icon={Icon('plus.svg')}
              onClick={() => setShowNewFile('Novo anexo')}
            >
              Anexo
            </DefaultButton>
            <DefaultButton
              small
              icon={Icon('plus.svg')}
              onClick={() => setShowNewFile('Novo contrato')}
            >
              Contrato
            </DefaultButton>
          </div>
        </div>
        <div style={{ marginTop: 32, marginBottom: 32 }}>
          {showNewFile && (
            <UploadFile2
              edit
              data-testid="new-upload-file"
              options={opt}
              onClickRemoveFile={() => setShowNewFile(undefined)}
              nameFilePlaceholder={showNewFile}
              onClickTrash={() => setShowNewFile(undefined)}
              onClickCheck={(fileName: string, file: File) => {
                if (!fileName) return
                handleAddFile(showNewFile, fileName, file)
                setShowNewFile(undefined)
              }}
            />
          )}
        </div>

        {attachments.length > 0 && (
          <>
            <div className="new-token-subtitle">
              <h4>Anexos</h4>
            </div>
            <FilesContainer className="documents-container">
              {renderUploadFile(attachments, 'attachment')}
            </FilesContainer>
          </>
        )}
        {documentos.length > 0 && (
          <>
            <div className="new-token-subtitle">
              <h4>Documentos</h4>
            </div>
            <FilesContainer>
              {renderUploadFile(documentos, 'document')}
            </FilesContainer>
          </>
        )}
        {contratos.length > 0 && (
          <>
            <div className="new-token-subtitle">
              <h4>Contratos</h4>
            </div>
            <FilesContainer>
              {renderUploadFile(contratos, 'contract')}
            </FilesContainer>
          </>
        )}
        <ButtonNext isAdvance advancePage={() => handleSubmit()} />
      </Form>
    </Container>
  )
}
